/**
 * Asked once, when the author marks text for encryption: who decides when this opens,
 * and how?
 *
 * The three are not variations of one thing. A sentence-locked passage opens for
 * everyone the day its sentence is published. A character passage is never opened by a
 * key at all — its character decides, and only they can. An author-held passage is the
 * same shape, with the author holding it: no sentence, no ceremony, one button on the
 * day he is ready.
 */

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import React, { useEffect, useState } from 'react'

export interface CharacterOption {
  tagId: string
  name: string
}

/** Sentinel value for the "new character" row — no tag id can collide with it. */
const ADD_CHARACTER = '__add__'

interface EncryptionKindDialogProps {
  open: boolean
  characters: CharacterOption[]
  onClose: () => void
  /**
   * Add a character to the book by name and return it, so the author never has to
   * leave a half-marked selection to go open the tag manager.
   */
  onCreateCharacter?: (name: string) => Promise<CharacterOption>
  /** Exactly one of the two: a character to belong to, or the sentence that locks it. */
  onConfirm: (choice: { character?: string; phrase?: string; authorHeld?: boolean }) => void
}

export const EncryptionKindDialog: React.FC<EncryptionKindDialogProps> = ({
  open,
  characters,
  onClose,
  onCreateCharacter,
  onConfirm,
}) => {
  const [kind, setKind] = useState<'sentence' | 'character' | 'author'>('sentence')
  const [character, setCharacter] = useState('')
  const [phrase, setPhrase] = useState('')
  const [added, setAdded] = useState<CharacterOption[]>([])
  const [newName, setNewName] = useState('')
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState('')

  useEffect(() => {
    if (open) {
      setKind('sentence')
      setCharacter('')
      setPhrase('')
      setAdded([])
      setNewName('')
      setCreating(false)
      setCreateError('')
    }
  }, [open])

  // Characters created in this dialog are held locally: the list came from the server
  // when the dialog opened, and refetching it just to see a name we already have would
  // discard the rest of the author's half-made choice.
  const options = [...characters, ...added]
  const addingCharacter = character === ADD_CHARACTER

  const createCharacter = async () => {
    const name = newName.trim()
    if (!name || !onCreateCharacter) {
      return
    }
    setCreating(true)
    setCreateError('')
    try {
      const created = await onCreateCharacter(name)
      setAdded((prev) => (prev.some((c) => c.tagId === created.tagId) ? prev : [...prev, created]))
      setCharacter(created.tagId)
      setNewName('')
    } catch (err: any) {
      setCreateError(err?.message || 'לא הצלחנו להוסיף את הדמות')
    } finally {
      setCreating(false)
    }
  }

  const ready =
    kind === 'sentence' ? Boolean(phrase.trim()) : kind === 'author' ? true : Boolean(character) && !addingCharacter

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth dir="rtl">
      <DialogTitle sx={{ textAlign: 'right' }}>מי מחליט על הקטע הזה?</DialogTitle>
      <DialogContent>
        <RadioGroup
          value={kind}
          onChange={(e) => setKind(e.target.value as 'sentence' | 'character' | 'author')}
          sx={{ '& .MuiFormControlLabel-root': { ml: 0, mr: -1 } }}
        >
          <FormControlLabel
            value="sentence"
            control={<Radio />}
            label={
              <Stack>
                <Typography variant="body2">נעול במשפט</Typography>
                <Typography variant="caption" color="text.secondary">
                  הקוראים רואים גליפים עד שתפרסם את המשפט
                </Typography>
              </Stack>
            }
          />
          <FormControlLabel
            value="author"
            control={<Radio />}
            label={
              <Stack>
                <Typography variant="body2">אצלך, עד שתחליט</Typography>
                <Typography variant="caption" color="text.secondary">
                  הקוראים רואים גליפים. אין משפט — נפתח בלחיצה שלך
                </Typography>
              </Stack>
            }
          />
          <FormControlLabel
            value="character"
            control={<Radio />}
            disabled={options.length === 0 && !onCreateCharacter}
            label={
              <Stack>
                <Typography variant="body2">שייך לדמות</Typography>
                <Typography variant="caption" color="text.secondary">
                  רק אתה והדמות רואים. היא מחליטה אם לפתוח
                </Typography>
              </Stack>
            }
          />
        </RadioGroup>

        {kind === 'sentence' && (
          <TextField
            fullWidth
            sx={{ mt: 2 }}
            label="המשפט"
            value={phrase}
            onChange={(e) => setPhrase(e.target.value)}
            placeholder="המשפט שיפתח את הקטע"
            helperText="שמור אותו אצלך. ביום שתפרסם אותו, הקטע נפתח לכל מי שמחזיק בו."
          />
        )}

        {kind === 'character' && (
          <>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel id="encryption-character-label">דמות</InputLabel>
              <Select
                labelId="encryption-character-label"
                value={character}
                label="דמות"
                onChange={(e) => {
                  setCharacter(e.target.value)
                  setCreateError('')
                }}
              >
                {options.map((c) => (
                  <MenuItem key={c.tagId} value={c.tagId}>
                    {c.name}
                  </MenuItem>
                ))}
                {onCreateCharacter && (
                  <MenuItem value={ADD_CHARACTER}>
                    <Typography variant="body2" color="primary">
                      + דמות חדשה
                    </Typography>
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            {addingCharacter && (
              <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} alignItems="flex-start">
                <TextField
                  fullWidth
                  autoFocus
                  size="small"
                  label="שם הדמות"
                  value={newName}
                  onChange={(e) => {
                    setNewName(e.target.value)
                    setCreateError('')
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      void createCharacter()
                    }
                  }}
                  disabled={creating}
                  error={Boolean(createError)}
                  helperText={createError || 'נוספת לספר תחת ״דמויות״'}
                />
                <Button
                  variant="outlined"
                  onClick={() => void createCharacter()}
                  disabled={creating || !newName.trim()}
                  sx={{ mt: 0.25, flexShrink: 0 }}
                >
                  הוסף
                </Button>
              </Stack>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ flexDirection: 'row-reverse', justifyContent: 'flex-start', gap: 1, px: 3, pb: 2 }}>
        <Button onClick={onClose}>ביטול</Button>
        <Button
          variant="contained"
          disabled={!ready}
          onClick={() =>
            onConfirm(
              kind === 'character'
                ? { character }
                : kind === 'author'
                  ? { authorHeld: true }
                  : { phrase: phrase.trim() },
            )
          }
        >
          סמן להצפנה
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default EncryptionKindDialog
